// components/ItemsTable.tsx
import React, { useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Chip,
  Button,
  Collapse,
  IconButton,
  Tooltip,
  Alert,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Search,
  ExpandMore,
  ExpandLess,
  Warning,
  CheckCircle,
  Error as ErrorIcon
} from '@mui/icons-material';
import AutoFocusScanner, { AutoFocusScannerRef } from './ScannerComponent';

// Types
interface ShipmentItemDto {
  id: string;
  shipmentId: string;
  unitId?: string;
  barcode: string;
  expectedQuantity: number;
  receivedQuantity: number;
  discrepancyQuantity: number;
  discrepancyType?: number | null;
  discrepancyTypeName?: string;
  boxReferences?: string[];
  unitDetails?: {
    id: string;
    itemId: string;
    itemName: string;
    barcode: string;
    symbol: string;
    unitOfMeasurement: string;
    itemAdditionalProperties?: {
      [key: string]: any;
    };
    inventoryLocations?: Array<{
      warehouseId: string;
      warehouseName: string;
      storageUnitId: string;
      storageUnitName: string;
      quantity: number;
    }>;
  };
  createdAt: string;
  updatedAt: string;
}

interface ItemsTableProps {
  items: ShipmentItemDto[];
  itemSearchTerm: string;
  onItemSearchChange: (value: string) => void;
  autoFocus?: boolean;
  showAdditionalProperties?: boolean;
  totalCount?: number;
  page?: number;
  rowsPerPage?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  onFilterDiscrepancies?: (onlyDiscrepancies: boolean) => void;
  showDiscrepanciesOnly?: boolean;
}

const ItemsTable: React.FC<ItemsTableProps> = ({
  items,
  itemSearchTerm,
  onItemSearchChange,
  autoFocus = false,
  showAdditionalProperties = false,
  totalCount = 0,
  page = 0,
  rowsPerPage = 50,
  onPageChange,
  onRowsPerPageChange,
  onFilterDiscrepancies,
  showDiscrepanciesOnly = false
}) => {
  const searchRef = useRef<AutoFocusScannerRef>(null);
  const [expandedItems, setExpandedItems] = React.useState<Set<string>>(new Set());

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (autoFocus) {
      const timer = setTimeout(() => {
        searchRef.current?.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'i') {
          event.preventDefault();
          searchRef.current?.focus();
        }
      };
  
      document.addEventListener('keydown', handleKeyDown);
      
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, []);

  // Get discrepancy status configuration
  const getDiscrepancyConfig = (item: ShipmentItemDto) => {
    if (item.discrepancyQuantity === 0) {
      return {
        icon: <CheckCircle fontSize="small" />,
        label: 'Match',
        color: 'success' as const,
        bgcolor: '#e8f5e9',
        textColor: '#2e7d32'
      };
    } else if (item.discrepancyQuantity > 0) {
      return {
        icon: <Warning fontSize="small" />,
        label: 'Overage',
        color: 'warning' as const,
        bgcolor: '#fff3e0',
        textColor: '#f57c00'
      };
    } else {
      return {
        icon: <ErrorIcon fontSize="small" />,
        label: 'Missing',
        color: 'error' as const,
        bgcolor: '#ffebee',
        textColor: '#c62828'
      };
    }
  };

  const handleSearch = (value: string) => {
  };

  // Count discrepancies
  const discrepancyCount = items.filter(item => item.discrepancyQuantity !== 0).length;

  return (
    <Card>
      <CardHeader
        title="Shipment Items"
        // subheader={`Total: ${totalCount || items.length}`}
        action={
          onFilterDiscrepancies && discrepancyCount > 0 ? (
            <FormControlLabel
              control={
                <Switch
                  checked={showDiscrepanciesOnly}
                  onChange={(e) => onFilterDiscrepancies(e.target.checked)}
                  color="error"
                />
              }
              label={
                <Typography variant="body2" color="error">
                  Show Discrepancies Only
                </Typography>
              }
            />
          ) : null
        }
      />
      <CardContent>
        <Stack spacing={2}>
          {/* Items Search - Auto-focused */}
          <AutoFocusScanner
            ref={searchRef}
            fullWidth
            size="small"
            placeholder="Search items by barcode, item name, or symbol... (Ctrl+I)"
            value={itemSearchTerm}
            onChange={onItemSearchChange}
            onScan={handleSearch}
            autoFocus={autoFocus}
            autoSelect={true}
            clearAfterScan={false}
            scannerIcon={<Search />}
            focusDelay={300}
            helperText="💡 Tip: This field auto-focuses when you switch to Details tab"
          />

          {items.length > 0 ? (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ maxHeight: 600 }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell width="5%"><strong>Status</strong></TableCell>
                    <TableCell width="25%"><strong>Item Details</strong></TableCell>
                    {showAdditionalProperties && <TableCell width="20%"><strong>Properties</strong></TableCell>}
                    <TableCell align="center" width="12%"><strong>Expected</strong></TableCell>
                    <TableCell align="center" width="12%"><strong>Received</strong></TableCell>
                    <TableCell align="center" width="12%"><strong>Discrepancy</strong></TableCell>
                    <TableCell width="14%"><strong>Boxes</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => {
                    const discrepancyConfig = getDiscrepancyConfig(item);
                    const hasDiscrepancy = item.discrepancyQuantity !== 0;
                    return (
                      <TableRow
                        key={item.id}
                        hover
                        sx={{
                          bgcolor: hasDiscrepancy ? `${discrepancyConfig.bgcolor}15` : 'inherit',
                          '&:hover': {
                            bgcolor: hasDiscrepancy ? `${discrepancyConfig.bgcolor}30` : undefined
                          }
                        }}
                      >
                        {/* Status Icon */}
                        <TableCell>
                          <Tooltip title={discrepancyConfig.label}>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              {discrepancyConfig.icon}
                            </Box>
                          </Tooltip>
                        </TableCell>

                        {/* Item Details */}
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="600">
                              {item.unitDetails?.itemName || `Item ${item.barcode}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {item.unitDetails?.symbol || item.barcode}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {item.barcode}
                            </Typography>
                          </Box>
                        </TableCell>
                        {showAdditionalProperties && (
                          <TableCell>
                            {item.unitDetails?.itemAdditionalProperties ? (
                              <Box>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Stack direction="row" spacing={0.5}>
                                    {Object.entries(item.unitDetails.itemAdditionalProperties).slice(0, 2).map(([key, value]) => {
                                      if (!value || value === '' || value === null || value === undefined) return null;
                                      return (
                                        <Chip
                                          key={key}
                                          label={`${key}: ${value}`}
                                          size="small"
                                          variant="outlined"
                                          color={key === 'Brand' ? 'primary' : key === 'Color' ? 'secondary' : key === 'size' ? 'success' : 'default'}
                                          sx={{ fontSize: '0.65rem', height: '18px' }}
                                        />
                                      );
                                    })}
                                  </Stack>
                                  {Object.keys(item.unitDetails.itemAdditionalProperties).filter(key => {
                                    const value = item.unitDetails!.itemAdditionalProperties![key];
                                    return value && value !== '' && value !== null && value !== undefined;
                                  }).length > 2 && (
                                    <Tooltip title={expandedItems.has(item.id) ? 'Show less' : 'Show more properties'}>
                                      <IconButton
                                        size="small"
                                        onClick={() => toggleExpanded(item.id)}
                                      >
                                        {expandedItems.has(item.id) ? <ExpandLess /> : <ExpandMore />}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                                <Collapse in={expandedItems.has(item.id)}>
                                  <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap">
                                    {Object.entries(item.unitDetails.itemAdditionalProperties).slice(2).map(([key, value]) => {
                                      if (!value || value === '' || value === null || value === undefined) return null;
                                      return (
                                        <Chip
                                          key={key}
                                          label={`${key}: ${value}`}
                                          size="small"
                                          variant="outlined"
                                          color={key === 'Brand' ? 'primary' : key === 'Color' ? 'secondary' : key === 'size' ? 'success' : 'default'}
                                          sx={{ fontSize: '0.65rem', height: '18px', mb: 0.5 }}
                                        />
                                      );
                                    })}
                                  </Stack>
                                </Collapse>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                No properties
                              </Typography>
                            )}
                          </TableCell>
                        )}

                        {/* Expected Quantity */}
                        <TableCell align="center">
                          <Chip
                            label={item.expectedQuantity}
                            size="small"
                            color="default"
                            sx={{ fontWeight: 600, minWidth: '50px' }}
                          />
                        </TableCell>

                        {/* Received Quantity */}
                        <TableCell align="center">
                          <Chip
                            label={item.receivedQuantity}
                            size="small"
                            color="primary"
                            sx={{ fontWeight: 600, minWidth: '50px' }}
                          />
                        </TableCell>

                        {/* Discrepancy Quantity */}
                        <TableCell align="center">
                          <Chip
                            icon={hasDiscrepancy ? discrepancyConfig.icon : undefined}
                            label={item.discrepancyQuantity > 0 ? `+${item.discrepancyQuantity}` : item.discrepancyQuantity}
                            size="small"
                            color={discrepancyConfig.color}
                            sx={{
                              fontWeight: 600,
                              minWidth: '60px',
                              bgcolor: hasDiscrepancy ? discrepancyConfig.bgcolor : undefined,
                              color: hasDiscrepancy ? discrepancyConfig.textColor : undefined
                            }}
                          />
                        </TableCell>

                        {/* Box References */}
                        <TableCell>
                          {item.boxReferences && item.boxReferences.length > 0 ? (
                            <Box>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                {item.boxReferences.slice(0, 2).map((boxRef, idx) => (
                                  <Chip
                                    key={idx}
                                    label={boxRef}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: '20px' }}
                                  />
                                ))}
                              </Stack>
                              {item.boxReferences.length > 2 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{item.boxReferences.length - 2} more
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No boxes
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Search sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
              <Typography color="text.secondary">
                {items.length === 0
                  ? 'No items scanned yet'
                  : 'No items match your search'}
              </Typography>
            </Box>
          )}

          {/* Pagination */}
          {onPageChange && onRowsPerPageChange && totalCount > 0 && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => onPageChange(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
              rowsPerPageOptions={[25, 50, 100]}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default React.memo(ItemsTable);